import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    aiTariffAbEnabled,
    closeLimit,
    PROMO_ID,
    showInterval,
    showLimit,
    storageKeyForBanner,
} from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

import { SUMMER_PROMO_2026_MODAL_QUERY_PARAM } from './summerCampaign2026ModalCreator';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/AiTariffPromoDialog/AiTariffPromoDialog'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const aiTariffPromoCreator: ModelCreator = ({ models, render, stores }) =>
    new PopupModel(
        PROMO_ID,
        models.base,
        new BaseModelProps(PROMO_ID, {
            store: new LocalStorageStore(storageKeyForBanner, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit,
            closeLimit,
            showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: false,
            test: (resolve, reject) => {
                if (IS_PHONE_BROWSER || !aiTariffAbEnabled) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (storage !== EStorageType.quotaCleaner) {
                    reject();
                    return;
                }

                const queryParams = SettingsSelectors.getQueryParams(state);
                const searchParams = new URLSearchParams(window.location.search);

                const { action = '' } = queryParams;
                if (action) {
                    reject();
                    sendXray(['promo-test', PROMO_ID, 'rejectbyaction']);
                    return;
                }

                // Не показываем поверх акционной модалки летней акции, открытой по прямой ссылке
                if (searchParams.get(SUMMER_PROMO_2026_MODAL_QUERY_PARAM)) {
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isOverquota = UserQuotaSelectors.isOverquota(state);

                const isReject = isBizUser || isPaidUser || isOverquota || isCorpUser;

                sendXray(['promo-test', PROMO_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
